import { Box } from '@chakra-ui/react';
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useShowToast } from '@/hooks';
import { FormBuilder } from '@/components/formBuilder';
import { SectionWithHeading } from '@/ui/SectionHeading';
import { SelectOptionWithAvatar } from '@/components/formBuilder/SelectComponents';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useLazyGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { checkForInvalidVariablesInSubjectAndBody } from '@/utils/commonHelpers';
import {
  INTEGRATION_TYPES,
  RADIO_OPTIONS,
  TEMPLATE_VARIABLES_VIEW,
  ESIGN_EDITOR_PLUGINS,
  ESIGN_SUBJECT_MAX_LENGTH,
  ESIGN_MESSAGES,
  USE_TEXT_EDITOR,
} from '@/pages/settings-revamp/modules/ESignatureSettings/esignature.config';
import { scrollToInvalidVariablesAlert } from '@/pages/settings-revamp/modules/ESignatureSettings/esignature.utils';
import { useOfferStore } from '../../store';
import { useGetDefaultESignQuery } from '@/store/query/e-signs';

export interface EsignEmailFormRef {
  getModel: () => Promise<{
    hasValidData: boolean;
    payload: any;
  }>;
}

export interface EsignEmailFormProps {
  integrationType: 'docusign' | 'pandadocs' | 'adobe';
  isAdmin?: boolean;
}

const getIntegrationTypeParam = (integrationType: string) => {
  if (integrationType === INTEGRATION_TYPES.PANDADOC) {
    return `${integrationType}/pandadocs_api`;
  }
  if (integrationType === INTEGRATION_TYPES.ADOBE) {
    return `${integrationType}/adobe_api`;
  }
  if (integrationType === INTEGRATION_TYPES.DOCUSIGN) {
    return `${integrationType}/docusign_api`;
  }
  return undefined;
};

export const EsignEmailForm = forwardRef<
  EsignEmailFormRef,
  EsignEmailFormProps
>(({ integrationType, isAdmin = false }, comRef) => {
  const formRef = useRef<any>(null);
  const toast = useShowToast();
  const applicationData = useOfferStore((state) => state.applicationData);

  const integrationTypeParam = useMemo(
    () => getIntegrationTypeParam(integrationType),
    [integrationType]
  );

  const jobId = applicationData?.job_id;
  const queryParams = integrationTypeParam
    ? { integration_type: integrationTypeParam }
    : {};
  const jobIdParam = jobId ? { job_id: jobId } : {};

  const { data, isFetching } = useGetDefaultESignQuery({
    ...queryParams,
    ...jobIdParam,
  });

  const [getVariables, { data: { variables = [] } = {} }] =
    useLazyGetTemplatesVariablesQuery();

  const usersList = useSelector(selectUsers);

  const variablesListArr = useMemo(
    () => Object.entries(variables || {}).flatMap(([, values = []]) => values),
    [variables]
  );

  const {
    company_signer_required,
    signing_order,
    company_signer,
    email_body,
    email_subject,
  } = data || {};

  useEffect(() => {
    getVariables({ params: { view: TEMPLATE_VARIABLES_VIEW } });
  }, [getVariables]);

  const useTextEditor = useMemo(() => {
    return USE_TEXT_EDITOR[integrationType] ?? USE_TEXT_EDITOR.docusign;
  }, [integrationType]);

  const pluginsConfig = useMemo(() => {
    if (integrationType in ESIGN_EDITOR_PLUGINS) {
      return ESIGN_EDITOR_PLUGINS[integrationType];
    }
    return ESIGN_EDITOR_PLUGINS.docusign;
  }, [integrationType]);

  const subjectMaxLength = useMemo(() => {
    return (
      ESIGN_SUBJECT_MAX_LENGTH[integrationType] ||
      ESIGN_SUBJECT_MAX_LENGTH.docusign
    );
  }, [integrationType]);

  const emailFieldConfigs = useEmailField({
    formRef,
    showFromField: false,
    showBodyLabel: false,
    replaceContent: false,
    getEditorInstance: useTextEditor,
    isBodyRequired: false,
    pluginsConfig: useTextEditor ? [...pluginsConfig] : [],
    variables: variables,
    subjectMaxLength,
    hideToolbar: !useTextEditor,
    showVariablesOnRight: integrationType === INTEGRATION_TYPES.ADOBE,
  });

  useImperativeHandle(comRef, () => ({
    getModel: async () => {
      if (!formRef.current) {
        return {
          hasValidData: false,
          payload: {},
        };
      }

      const form = formRef.current;
      const { values } = form || {};
      const {
        subject,
        body,
        company_signer_id,
        company_signer_required,
        signing_order,
      } = values || {};

      if (!subject) {
        toast({
          title: ESIGN_MESSAGES.SUBJECT_REQUIRED,
          status: 'error',
        });
        return {
          hasValidData: false,
          payload: {},
        };
      }

      const isInvalidVariablesFound = checkForInvalidVariablesInSubjectAndBody({
        variablesListArr,
        body,
        subject,
        variablesListSubject: variablesListArr.filter(
          (variable) => variable !== 'sender.email_signature'
        ),
      });

      if (isInvalidVariablesFound) {
        scrollToInvalidVariablesAlert();
        return {
          hasValidData: false,
          payload: {},
        };
      }

      const payload: any = {
        esign_envelope: {
          email_subject: subject || '',
          email_body: body || '',
        },
      };

      if (company_signer_required === '1' && company_signer_id) {
        payload.esign_envelope.company_signer_id = company_signer_id;
      }
      if (company_signer_required === '1' && signing_order !== undefined) {
        payload.esign_envelope.signing_order =
          signing_order === '1' ? 'company_signer_first' : 'candidate_first';
      }

      return {
        hasValidData: true,
        payload,
      };
    },
  }));

  const createSigningDetailsFields = () => [
    isAdmin && {
      name: 'company_signer_required',
      label: 'Does anyone from the employer need to sign?',
      styles: { spacing: '3rem' },
      type: 'radio2',
      radioStyles: { variant: 'ats-primary-sm' },
      options: RADIO_OPTIONS.YES_NO,
    },
    {
      name: 'company_signer_id',
      label: isAdmin
        ? 'Choose who signs on behalf of the employer'
        : 'Company signer',
      isRequired: true,
      type: 'select',
      options: usersList,
      customFormatLabel: SelectOptionWithAvatar,
      valueKey: 'id',
      labelKey: 'name',
      isConditional: true,
      conditionConfig: {
        type: 'and',
        conditions: [{ field: 'company_signer_required', value: '1' }],
      },
    },
    {
      name: 'signing_order',
      label: 'Order of signing',
      radioStyles: { variant: 'ats-primary-sm' },
      styles: { spacing: '3rem' },
      type: 'radio2',
      isConditional: true,
      disabled: !isAdmin,
      conditionConfig: {
        type: 'and',
        conditions: [{ field: 'company_signer_required', value: '1' }],
      },
      options: RADIO_OPTIONS.SIGNING_ORDER,
    },
  ];

  const offerSectionProps = {
    background: 'secondary-50',
    containerStyles: {
      mb: '16px',
      padding: '5px',
    },
    contentStyles: {
      bg: 'white',
      borderRadius: '0 0 6px 6px',
    },
    textProps: {
      fontSize: '26px',
    },
    headingProps: {
      mb: '16px',
    },
  };

  const formConfig = [
    (company_signer_required || isAdmin) && {
      id: 'signingDetails',
      type: 'wrapper',
      Wrapper: SectionWithHeading,
      wrapperProps: {
        title: 'Details',
        ...offerSectionProps,
      },
      fields: createSigningDetailsFields(),
    },
    {
      id: 'emailDetails',
      type: 'wrapper',
      Wrapper: SectionWithHeading,
      wrapperProps: {
        title: 'Message',
        ...offerSectionProps,
      },
      fields: [...emailFieldConfigs],
    },
  ].filter(Boolean);

  const formInitialValues = {
    body: email_body,
    subject: email_subject,
    company_signer_id: company_signer?.id,
    company_signer_required: company_signer_required ? '1' : '0',
    signing_order: signing_order === 'company_signer_first' ? '1' : '0',
  };

  if (isFetching) {
    return null;
  }

  return (
    <Box w="100%">
      <FormBuilder
        initialValues={formInitialValues}
        config={formConfig}
        onSubmit={() => {}}
        formRef={formRef}
      />
    </Box>
  );
});
